import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Icon } from 'semantic-ui-react';
import TailwindLocationEditor from 'widgets/bussiness/location-editor-tw';
import { DateTimePeriod } from 'stores/events/events-store';
import { SupportDetails } from 'stores/requests/requests-store';
import TailwindPositionCodeEditor from 'widgets/bussiness/position-code-editor-tw';
import TailwindPeriodInput from 'widgets/form/period-input-tw';

interface TailwindRequestSupportDetailsProps extends WithTranslation {
  isForm?: boolean;
  readOnly: boolean;
  onChange: (values) => void;
  supportDetails: SupportDetails;
  startDate: string;
  endDate: string;
}

const defaultSupportPosition = {
  locationId: null,
  location: null,
  estimatedCost: 0
};

class TailwindRequestSupportDetails extends Component<TailwindRequestSupportDetailsProps> {
  getRawSupportPosition = () => {
    const { startDate, endDate } = this.props;
    return {
      comment: '',
      duration: '',
      endDate: endDate,
      startDate: startDate,
      locationA: { estimatedCost: 0, location: null, locationId: null },
      locationB: { estimatedCost: 0, location: null, locationId: null },
      locationC: { estimatedCost: 0, location: null, locationId: null },
      locationD: { estimatedCost: 0, location: null, locationId: null },
      positionCode: null,
      positionCodeName: null,
      requestedHC: 0
    };
  };

  handleNewSupportPosition = () => {
    const supportDetails = this.props.supportDetails;
    let newSupportPosition;
    if ((supportDetails.supportPositions || []).length <= 0) {
      newSupportPosition = { ...this.getRawSupportPosition() };
    } else {
      const defaultPosition = supportDetails.supportPositions[0];
      newSupportPosition = {
        ...this.getRawSupportPosition(),
        locationA: { ...defaultPosition.locationA, estimatedCost: 0 },
        locationB: { ...defaultPosition.locationB, estimatedCost: 0 },
        locationC: { ...defaultPosition.locationC, estimatedCost: 0 },
        locationD: { ...defaultPosition.locationD, estimatedCost: 0 }
      };
    }
    this.props.onChange({ supportPositions: [...supportDetails.supportPositions, newSupportPosition] });
  };

  removeSupportPosition = (index: number) => {
    const supportPositions = this.props.supportDetails.supportPositions.filter((_, i) => i !== index);
    this.props.onChange({ supportPositions });
  };

  positionCodeEditorChange = (property: string, value: any, index: number) => {
    const supportPositions = this.props.supportDetails.supportPositions.map((supportPos, idx) => {
      if (idx === index) {
        supportPos = { ...supportPos, [property]: value ? value.id : value };
      }
      return supportPos;
    });
    this.props.onChange({ supportPositions });
  };

  locationsEditorChange = (location: string, value: any) => {
    const supportDetails = this.props.supportDetails;
    const supportPositions = supportDetails.supportPositions.map(supportPos => {
      if (supportPos[location])
        supportPos = {
          ...supportPos,
          [location]: {
            ...supportPos[location],
            locationId: value ? value.id : value,
            estimatedCost: !value ? 0 : supportPos[location].estimatedCost
          }
        };
      else supportPos[location] = { locationId: value ? value.id : value, location: null, estimatedCost: 0 };
      return supportPos;
    });
    const changes = { supportPositions };
    const changeTotal = 'totalCost' + this.capitalize(location);
    let total = 0;
    if (value != null) {
      total = supportPositions.reduce((prevVal, acc) => prevVal + acc[location].estimatedCost, 0);
      changes[changeTotal] = total;
    }
    this.props.onChange({ supportPositions });
    this.props.onChange({ [changeTotal]: total });
  };

  supplyingHandler = (location: string, value: number, supportPosIdx: number) => {
    const supportDetails = this.props.supportDetails;
    const supportPositions = supportDetails.supportPositions.map((supportPos, i) => {
      if (supportPosIdx === i) supportPos = { ...supportPos, [location]: { ...supportPos[location], estimatedCost: value } };
      return supportPos;
    });
    const total = supportPositions.reduce((prevVal, acc) => prevVal + acc[location].estimatedCost, 0);
    const changeTotal = 'totalCost' + this.capitalize(location);
    this.props.onChange({ supportPositions });
    this.props.onChange({ [changeTotal]: total });
  };

  capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

  inputChangeHandler = (property: string, idx: number, value: string) => {
    const supportPositions = this.props.supportDetails.supportPositions.map((supportPos, i) => {
      if (i === idx) {
        supportPos = { ...supportPos, [property]: value };
      }
      return supportPos;
    });
    this.props.onChange({ supportPositions });
  };

  datesChangeHandler = ({ from, to }: DateTimePeriod, idx: number) => {
    const { startDate, endDate } = this.props.supportDetails.supportPositions[idx];
    let supportPositions;
    if (startDate && from !== startDate) {
      supportPositions = this.props.supportDetails.supportPositions.map((supportPos, i) => {
        if (i === idx) supportPos = { ...supportPos, startDate: from };
        return supportPos;
      });
    } else if (endDate && to !== endDate) {
      supportPositions = this.props.supportDetails.supportPositions.map((supportPos, i) => {
        if (i === idx) supportPos = { ...supportPos, endDate: to };
        return supportPos;
      });
    } else return;
    this.props.onChange({ supportPositions });
  };

  render() {
    const { t, readOnly, supportDetails } = this.props;
    const {
      totalCostLocationA,
      totalCostLocationB,
      totalCostLocationC,
      totalCostLocationD,
      requestPreApprovedDuringASP,
      supportPositions
    } = supportDetails;

    return (
      <>
        <div className="flex items-center gap-4">
          <label className="text-sm text-gray-700">{t('Request pre-approved during ASP')}</label>
          <div className="flex items-center">
            <input
              id="default-checkbox"
              type="checkbox"
              value=""
              checked={requestPreApprovedDuringASP}
              className="w-4 h-4 text-black-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              onChange={checked => this.props.onChange({ requestPreApprovedDuringASP: checked })}
              disabled={readOnly}
            />
          </div>
        </div>
        <button
          disabled={readOnly}
          onClick={this.handleNewSupportPosition}
          className={`w-full ${readOnly ? 'bg-gray-600' : 'bg-primary-600'} text-white py-2 rounded mt-2`}
        >
          {t('Add Support Position')}
        </button>

        <div>
          <label className="text-sm text-gray-700">{t('Total Cost Location A')}</label>
          <div className="flex items-center gap-2">
            <input className="w-full border border-gray-300 rounded px-2 py-1" type="number" disabled value={totalCostLocationA} />
            <span className="whitespace-nowrap">$</span>
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-700">{t('Total Cost Location B')}</label>
          <div className="flex items-center gap-2">
            <input className="w-full border border-gray-300 rounded px-2 py-1" type="number" disabled value={totalCostLocationB} />
            <span className="whitespace-nowrap">$</span>
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-700">{t('Total Cost Location C')}</label>
          <div className="flex items-center gap-2">
            <input className="w-full border border-gray-300 rounded px-2 py-1" type="number" disabled value={totalCostLocationC} />
            <span className="whitespace-nowrap">$</span>
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-700">{t('Total Cost Location D')}</label>
          <div className="flex items-center gap-2">
            <input className="w-full border border-gray-300 rounded px-2 py-1" type="number" disabled value={totalCostLocationD} />
            <span className="whitespace-nowrap">$</span>
          </div>
        </div>

        {(supportPositions || []).length > 0 &&
          supportPositions.map(
            ({ positionCode, comment, requestedHC, duration, locationA, locationB, locationC, locationD, startDate, endDate }, i) => {
              locationA = locationA || { ...defaultSupportPosition };
              locationB = locationB || { ...defaultSupportPosition };
              locationC = locationC || { ...defaultSupportPosition };
              locationD = locationD || { ...defaultSupportPosition };
              return (
                <div key={i} className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div>
                    <label className="text-sm text-gray-700">{t('Free text from Request')}</label>
                    <input
                      className="w-full border border-gray-300 p-2 rounded bg-gray-100"
                      value={comment}
                      disabled={readOnly}
                      onChange={e => this.inputChangeHandler('comment', i, e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">{t('Position')}AQUI</label>
                    <TailwindPositionCodeEditor
                      readonly={readOnly}
                      placeholder={t('Position')}
                      value={positionCode}
                      showDescription
                      onChange={posCode => this.positionCodeEditorChange('positionCode', posCode, i)}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">{t('Requested HC')}</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 p-2 rounded"
                      value={requestedHC}
                      disabled={readOnly}
                      onChange={e => this.inputChangeHandler('requestedHC', i, e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">{t('Dates')}</label>
                    <TailwindPeriodInput
                      id={'request-suppport-details-' + i}
                      readOnly={readOnly}
                      onChange={period => this.datesChangeHandler(period, i)}
                      icon={false}
                      value={{ from: startDate, to: endDate }}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">{t('Duration')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        type="number"
                        disabled={readOnly}
                        onChange={e => this.inputChangeHandler('duration', i, e.target.value)}
                        value={duration}
                      />
                      <span className="whitespace-nowrap">w/d</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-700">{t('Supplying Location A')}</label>
                    <TailwindLocationEditor
                      placeholder={t('Location')}
                      nullable
                      clearable
                      readOnly={i !== 0 || readOnly}
                      value={locationA?.locationId ? { id: locationA.locationId, title: '' } : null}
                      onChange={location => this.locationsEditorChange('locationA', location)}
                      showCountry={true}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">{t('Estimated Cost Location A')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        type="number"
                        disabled={!locationA.locationId || readOnly}
                        onChange={e => this.supplyingHandler('locationA', Number(e.target.value), i)}
                        value={locationA.estimatedCost}
                      />
                      <span className="whitespace-nowrap">$</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-700">{t('Supplying Location B')}</label>
                    <TailwindLocationEditor
                      placeholder={t('Location')}
                      nullable
                      clearable
                      readOnly={i !== 0 || readOnly}
                      value={locationB?.locationId ? { id: locationB.locationId, title: '' } : null}
                      onChange={location => this.locationsEditorChange('locationB', location)}
                      showCountry={true}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">{t('Estimated Cost Location B')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        type="number"
                        disabled={!locationB.locationId || readOnly}
                        onChange={e => this.supplyingHandler('locationB', Number(e.target.value), i)}
                        value={locationB.estimatedCost}
                      />
                      <span className="whitespace-nowrap">$</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-700">{t('Supplying Location C')}</label>
                    <TailwindLocationEditor
                      placeholder={t('Location')}
                      nullable
                      clearable
                      readOnly={i !== 0 || readOnly}
                      value={locationC?.locationId ? { id: locationC.locationId, title: '' } : null}
                      onChange={location => this.locationsEditorChange('locationC', location)}
                      showCountry={true}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">{t('Estimated Cost Location C')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        type="number"
                        disabled={!locationC.locationId || readOnly}
                        onChange={e => this.supplyingHandler('locationC', Number(e.target.value), i)}
                        value={locationC.estimatedCost}
                      />
                      <span className="whitespace-nowrap">$</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-700">{t('Supplying Location D')}</label>
                    <TailwindLocationEditor
                      placeholder={t('Location')}
                      nullable
                      clearable
                      readOnly={i !== 0 || readOnly}
                      value={locationD?.locationId ? { id: locationD.locationId, title: '' } : null}
                      onChange={location => this.locationsEditorChange('locationD', location)}
                      showCountry={true}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">{t('Estimated Cost Location D')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        type="number"
                        disabled={!locationD.locationId || readOnly}
                        onChange={e => this.supplyingHandler('locationD', Number(e.target.value), i)}
                        value={locationD.estimatedCost}
                      />
                      <span className="whitespace-nowrap">$</span>
                    </div>
                  </div>
                  {!readOnly && <Icon color="red" name="remove" onClick={() => this.removeSupportPosition(i)} />}
                </div>
              );
            }
          )}
      </>
    );
  }
}

export default withTranslation()(TailwindRequestSupportDetails);
